import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Loader2Icon, PlusIcon, TrashIcon, BrainIcon } from "lucide-react";

export interface ReasoningSchemaField {
  key: string;
  instruction: string;
}

interface ReasoningAugmentModalProps {
  isVisible: boolean;
  onClose: () => void;
  onStartAugmentation: (schema: ReasoningSchemaField[]) => void;
  loading: boolean;
  datasetName?: string;
}

const ReasoningAugmentModal: React.FC<ReasoningAugmentModalProps> = ({
  isVisible,
  onClose,
  onStartAugmentation,
  loading,
  datasetName,
}) => {
  const [schema, setSchema] = useState<ReasoningSchemaField[]>([
    { key: "", instruction: "" },
  ]);

  useEffect(() => {
    if (isVisible) {
      setSchema([{ key: "", instruction: "" }]);
    }
  }, [isVisible]);

  const handleAddField = () => {
    setSchema([...schema, { key: "", instruction: "" }]);
  };

  const handleRemoveField = (index: number) => {
    if (schema.length > 1) {
      setSchema(schema.filter((_, i) => i !== index));
    }
  };

  const handleFieldChange = (
    index: number,
    field: "key" | "instruction",
    value: string
  ) => {
    const newSchema = [...schema];
    newSchema[index][field] = value;
    setSchema(newSchema);
  };

  const handleStartAugmentation = () => {
    const validSchema = schema.filter(
      (field) => field.key.trim() && field.instruction.trim()
    );
    if (validSchema.length > 0) {
      onStartAugmentation(validSchema);
    }
  };

  const isValid = schema.some(
    (field) => field.key.trim() && field.instruction.trim()
  );

  return (
    <Dialog open={isVisible} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BrainIcon className="w-5 h-5 text-maitai-green" />
            Reasoning Augmentation
          </DialogTitle>
          <DialogDescription>
            Define a structured reasoning schema to augment your dataset entries.
            {datasetName && (
              <span className="block mt-1 text-foreground font-medium">
                Dataset: {datasetName}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-gc-med">Reasoning Schema</Label>
            <p className="text-xs text-muted-foreground">
              Define the fields you want to extract from each request.
            </p>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {schema.map((field, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    value={field.key}
                    onChange={(e) => handleFieldChange(index, "key", e.target.value)}
                    placeholder="Field name (e.g., Intent)"
                    className="font-mono text-sm"
                  />
                </div>
                <div className="flex-[2]">
                  <Input
                    value={field.instruction}
                    onChange={(e) => handleFieldChange(index, "instruction", e.target.value)}
                    placeholder="Instruction (e.g., Determine what the user is asking for)"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveField(index)}
                  disabled={schema.length === 1}
                  className="h-9 w-9 text-muted-foreground hover:text-destructive shrink-0"
                >
                  <TrashIcon className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleAddField}
            className="gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            Add Field
          </Button>

          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-sm font-gc-med text-foreground">Example</p>
            <div className="text-xs text-muted-foreground space-y-1">
              <p><span className="font-mono text-foreground">Intent</span>: Determine what the user is asking for</p>
              <p><span className="font-mono text-foreground">Key Entities</span>: Identify the main subjects or objects mentioned</p>
              <p><span className="font-mono text-foreground">Reasoning</span>: Explain the logic needed to produce the correct response</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleStartAugmentation}
            disabled={loading || !isValid}
            className="gap-2"
          >
            {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
            Start Augmentation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReasoningAugmentModal;
